import React, { useState } from 'react';
import { useCommitments } from '../../hooks/useInvestQueries';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';
import AppLayout from '../../components/layouts/AppLayout';
import ResponsiveNavbar from '../../components/ResponsiveNavbar';
import { CheckCircle, XCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import PaymentDetailsDialog from '../../components/ui/PaymentDetailsDialog';
import RejectionReasonDialog from '../../components/ui/RejectionReasonDialog';

export default function AdminCommitments() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('submitted');
  const { data: commitments = [], isLoading: loading, error } = useCommitments(filter);

  if (error) {
    toast.error('Failed to load commitments');
  }

  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  const handleApprove = async (commitmentId) => {
    try {
      await api.post(`/commitments/${commitmentId}/approve`);
      toast.success('Commitment approved');
      queryClient.invalidateQueries(['commitments']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  const openRejectDialog = (commitment) => {
    setSelectedCommitment(commitment);
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async (reason) => {
    try {
      await api.post(`/commitments/${selectedCommitment.commitment_id}/reject`, null, { params: { reason } });
      toast.success('Commitment rejected');
      queryClient.invalidateQueries(['commitments']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error('Rejection failed');
    }
  };

  const openPaymentDialog = (commitment) => {
    setSelectedCommitment(commitment);
    setShowPaymentDialog(true);
  };

  const handlePaymentConfirm = async ({ method, reference }) => {
    try {
      await api.post(`/commitments/${selectedCommitment.commitment_id}/payment`, { status: 'received', method, reference });
      toast.success('Payment status updated to received');
      queryClient.invalidateQueries(['commitments']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error('Update failed');
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="admin-commitments-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={true} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Investment Commitments</h1>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6">
          {['submitted', 'approved', 'rejected'].map(status => (
            <button key={status} onClick={() => setFilter(status)} className={`px-4 py-2.5 sm:py-2 text-sm sm:text-base rounded-lg font-medium transition-colors ${filter === status ? 'bg-[#D4AF37] text-[#0A1128]' : 'bg-white/5 text-[#94A3B8] hover:bg-white/10'}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : commitments.length === 0 ? (
          <div className="premium-card p-12 text-center"><p className="text-[#64748B]">No {filter} commitments</p></div>
        ) : (
          <div className="space-y-4">
            {commitments.map(commit => (
              <div key={commit.commitment_id} className="premium-card p-6" data-testid={`commitment-${commit.commitment_id}`}>
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-['Outfit'] text-xl font-bold mb-1">{commit.asset_name}</h3>
                        <p className="text-[#94A3B8] text-sm">{commit.investor_name} ({commit.investor_email})</p>
                      </div>
                      <div className="text-right">
                        <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#D4AF37]">${commit.amount.toLocaleString()}</p>
                        <p className="text-[#94A3B8] text-sm">{commit.percentage}% ownership</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs border ${
                        commit.status === 'approved' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' :
                        commit.status === 'submitted' ? 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' :
                        'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30'
                      }`}>{commit.status}</span>
                      {commit.status === 'approved' && (
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${
                          commit.payment_status === 'received' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' :
                          commit.payment_status === 'processing' ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30 animate-pulse' :
                          'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30'
                        }`}>
                          Payment: {commit.payment_status === 'processing' ? 'Sent (Pending Review)' : commit.payment_status}
                        </span>
                      )}
                    </div>
                    <p className="text-[#64748B] text-xs">Submitted: {new Date(commit.submitted_at).toLocaleString()}</p>
                    {commit.rejection_reason && <p className="text-[#EF4444] text-sm mt-2">Reason: {commit.rejection_reason}</p>}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto mt-4 sm:mt-0">
                    {commit.status === 'submitted' && (
                      <>
                        <button onClick={() => handleApprove(commit.commitment_id)} className="w-full sm:w-auto px-3 py-2 sm:px-4 text-sm sm:text-base bg-[#10B981] text-white rounded-lg hover:bg-[#059669] transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-testid={`approve-commitment-btn-${commit.commitment_id}`}>
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button onClick={() => openRejectDialog(commit)} className="w-full sm:w-auto px-3 py-2 sm:px-4 text-sm sm:text-base bg-[#EF4444] text-white rounded-lg hover:bg-[#DC2626] transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-testid={`reject-commitment-btn-${commit.commitment_id}`}>
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}
                    {commit.status === 'approved' && (commit.payment_status === 'pending' || commit.payment_status === 'processing') && (
                      <button onClick={() => openPaymentDialog(commit)} className="w-full sm:w-auto px-3 py-2 sm:px-4 text-sm sm:text-base bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all flex items-center justify-center gap-2 whitespace-nowrap" data-testid={`mark-received-btn-${commit.commitment_id}`}>
                        <DollarSign className="w-4 h-4" />
                        Mark Received
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      <RejectionReasonDialog 
        isOpen={showRejectDialog} 
        onClose={() => setShowRejectDialog(false)} 
        onSubmit={handleRejectConfirm} 
      />
      
      <PaymentDetailsDialog 
        isOpen={showPaymentDialog} 
        onClose={() => setShowPaymentDialog(false)} 
        onSubmit={handlePaymentConfirm}
        title="Confirm Payment Receipt"
        submitLabel="Mark as Received"
      />
    </AppLayout>
  );
}
